import React, { useState, useEffect } from "react";
import { Polyline, Popup } from "react-leaflet";
import { useMap } from "react-leaflet";

const RoadRecordPath = ({ recording, index }) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map?.getZoom() || 18);


  // Listen to zoom changes to recalculate weight
  useEffect(() => {
    if (!map) return;

    const handleZoomEnd = () => {
      setZoom(map.getZoom());
    };

    map.on('zoomend', handleZoomEnd);

    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map]);
  // Check if recording has the expected structure
  if (!recording || typeof recording !== 'object') {
    return null;
  }

  // Convert points to Leaflet coordinate format [lat, lng]
  // Assuming position.y is latitude and position.x is longitude
  const pathCoordinates = recording.points?.map(point => {
    if (!point || !point.position || 
        typeof point.position.x !== 'number' || 
        typeof point.position.y !== 'number') {
      return null;
    }
    return [
      point.position.y, // latitude
      point.position.x  // longitude
    ];
  }).filter(coord => coord !== null) || [];

  // Use a single color for all road recordings
  const pathColor = "#FF6B35"; // Orange color for all recordings
  //"#3388ff" - blue color for all recordings
  // Calculate weight based on real-world meters and zoom level
  const calculateWeightForMeters = (meters = 2) => {
    if (!map) return 15; // Fallback to fixed weight
    
    const currentZoom = zoom; // Use state zoom for consistency
    const center = map.getCenter();
    
    // Calculate meters per pixel at current zoom level
    // This formula works for Web Mercator projection
    const metersPerPixel = (40075016.686 * Math.cos(center.lat * Math.PI / 180)) / Math.pow(2, currentZoom + 8);
    
    // Convert meters to pixels
    const weightInPixels = meters / metersPerPixel;
    
    // Ensure minimum and maximum weight for usability
    return Math.max(2, Math.min(50, weightInPixels));
  };

  const polylineWeight = calculateWeightForMeters(2); // 2 meters in real world

  // Skip rendering if there are no valid coordinates or less than 2 points
  if (!pathCoordinates || pathCoordinates.length < 2) {
    return null;
  }

  // Format timestamps for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    try {
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return timestamp.toString();
    }
  };

  const startTime = recording.points?.[0]?.timestamp;
  const endTime = recording.points?.[recording.points.length - 1]?.timestamp;

  return (
    <Polyline
      positions={pathCoordinates}
      pathOptions={{
        color: pathColor,
        weight: polylineWeight, // Dynamic weight based on real-world meters
        opacity: 0.9,
      }}
    >
      <Popup>
        <div style={{ 
          minWidth: "200px",
          backgroundColor: "rgba(255, 255, 255, 0.8)", // Transparent white background
          padding: "10px",
          borderRadius: "8px",
          border: "1px solid rgba(0, 0, 0, 0.1)"
        }}>
          <h4 style={{ margin: "0 0 10px 0", color: pathColor }}>
            Road Recording
          </h4>
          <div><strong>Cart Number:</strong> {recording.cart_number || "Unknown"}</div>
          <div><strong>Record ID:</strong> {recording.recordId || "Unknown"}</div>
          <div><strong>Points:</strong> {pathCoordinates.length}</div>
          <div><strong>Start Time:</strong> {formatTimestamp(startTime)}</div>
          <div><strong>End Time:</strong> {formatTimestamp(endTime)}</div>
          <div><strong>Last Updated:</strong> {formatTimestamp(recording.lastUpdated)}</div>
        </div>
      </Popup>
    </Polyline>
  );
};

export default React.memo(RoadRecordPath);
